import { c as _c } from "react/compiler-runtime";
import { ScrollShadow } from "../ui/scroll-shadow";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoPeopleOutline } from "react-icons/io5";
import { LuChevronDown } from "react-icons/lu";
import { RiChatNewLine } from "react-icons/ri";
import { TbBan } from "react-icons/tb";
import { ChatBulkActions, type ChatSelection, RowCheckbox, SelectAllRow, useChatSelection, useRowReveal } from "@/app/[tenant]/(console)/comms/chat/sidebar/chat-selection";
import { Button } from "@/components/ui";
import Badge from "@/components/ui/badge";
import Tooltip from "@/components/ui/tooltip";
import { useCommunication } from "@/contexts/chat-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { getSmsOptOutMessage } from "@/lib/chat-utils";
import { formatDateTime, formatName } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { ChatFilters, ChatSummary, isOptedOutOfSms } from "@/types/chat";
import { ChatStatus } from "@/types/enums/chat";
import { GLOBAL_CHAT_CONSTANTS } from "./chat-constants";
import { chatQueryOptions } from "@/query/comms/chat";
import { useChatFiltersStore } from "@/stores/chat-store";
interface MessagingChatRowProps {
  chat: ChatSummary;
  index: number;
  selection: ChatSelection;
  onOpen: () => void;
}
const MessagingChatRow = t0 => {
  const $ = _c(45);
  const {
    chat,
    index,
    selection,
    onOpen
  } = t0;
  const {
    revealed,
    handlers
  } = useRowReveal();
  let isChecked;
  let name;
  let t1;
  if ($[0] !== chat || $[1] !== selection.selectedIds) {
    isChecked = selection.selectedIds.has(chat.id);
    let t2;
    if ($[5] !== chat.patients) {
      t2 = chat.patients.length > 1 ? chat.patients.map(_temp).join(", ") : formatName(chat.patients[0].firstName, chat.patients[0].lastName);
      $[5] = chat.patients;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    name = t2;
    t1 = cx("my-1 flex cursor-pointer items-start rounded-md bg-background/90 px-3 py-2 transition-colors hover:bg-muted/80", isChecked && "bg-primary/10", isOptedOutOfSms(chat) && "opacity-60");
    $[0] = chat;
    $[1] = selection.selectedIds;
    $[2] = isChecked;
    $[3] = name;
    $[4] = t1;
  } else {
    isChecked = $[2];
    name = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[7] !== chat.id || $[8] !== index || $[9] !== isChecked || $[10] !== name || $[11] !== revealed || $[12] !== selection) {
    t2 = selection.canSelect && <RowCheckbox label={`Select ${name}`} checked={isChecked} revealed={revealed} isSelecting={selection.isSelecting} index={index} onToggle={() => selection.toggle(chat.id)} />;
    $[7] = chat.id;
    $[8] = index;
    $[9] = isChecked;
    $[10] = name;
    $[11] = revealed;
    $[12] = selection;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== name) {
    t3 = <h3 className="min-w-0 truncate text-body-emphasis">{name}</h3>;
    $[14] = name;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== chat.patients) {
    t4 = chat.patients.length > 1 && <Tooltip content={<div className="space-y-1"><div className="text-caption-strong">These patients share this phone number</div><ul className="space-y-1">{chat.patients.map(_temp2)}</ul></div>} showArrow={false}><Badge variant="neutral" icon={IoPeopleOutline} className="flex-shrink-0 gap-1 bg-transparent text-caption">{chat.patients.length}</Badge></Tooltip>;
    $[16] = chat.patients;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== chat) {
    t5 = isOptedOutOfSms(chat) && <Tooltip content={getSmsOptOutMessage(chat) ?? ""} showArrow={false}><TbBan className="h-4 w-4 flex-shrink-0 text-red-500" /></Tooltip>;
    $[18] = chat;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex min-w-0 flex-1 items-center gap-2">{t3}{t4}{t5}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== chat.status) {
    t7 = chat.status !== ChatStatus.READ && <div className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" />;
    $[24] = chat.status;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t6 || $[27] !== t7) {
    t8 = <div className="flex items-center justify-between">{t6}{t7}</div>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const t9 = chat.latestMessageContent || "No messages yet";
  let t10;
  if ($[29] !== t9) {
    t10 = <p className="line-clamp-2 text-caption text-foreground-secondary">{t9}</p>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== chat.latestMessageDate) {
    t11 = chat.latestMessageDate && formatDateTime(chat.latestMessageDate);
    $[31] = chat.latestMessageDate;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11) {
    t12 = <p className="text-caption text-foreground-muted">{t11}</p>;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t12 || $[37] !== t8) {
    t13 = <div className="min-w-0 flex-1">{t8}{t10}{t12}</div>;
    $[35] = t10;
    $[36] = t12;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== handlers || $[40] !== onOpen || $[41] !== t1 || $[42] !== t13 || $[43] !== t2) {
    t14 = <div className={t1} onClick={onOpen} {...handlers}>{t2}{t13}</div>;
    $[39] = handlers;
    $[40] = onOpen;
    $[41] = t1;
    $[42] = t13;
    $[43] = t2;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
};
const MessagingSidebar: React.FC = () => {
  const $ = _c(50);
  const {
    setIsAddChatOpen,
    openChat,
    chatFilters,
    setChatFilters
  } = useCommunication();
  const [localChatFilters, setLocalChatFilters] = useState(chatFilters);
  const previousChatFiltersRef = useRef(chatFilters);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = debounce(filters => {
      setLocalChatFilters(filters);
    }, 700);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const debouncedSetLocalChatFilters = t0;
  let t1;
  let t2;
  if ($[1] !== chatFilters) {
    t1 = () => {
      const previous = previousChatFiltersRef.current;
      const nonSearchFiltersUnchanged = previous.unreadOnly === chatFilters.unreadOnly && previous.unrespondedOnly === chatFilters.unrespondedOnly && previous.humanOnly === chatFilters.humanOnly && previous.locationId === chatFilters.locationId;
      if (nonSearchFiltersUnchanged) {
        debouncedSetLocalChatFilters(chatFilters);
      } else {
        debouncedSetLocalChatFilters.cancel();
        setLocalChatFilters(chatFilters);
      }
      previousChatFiltersRef.current = chatFilters;
    };
    t2 = [chatFilters, debouncedSetLocalChatFilters];
    $[1] = chatFilters;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      debouncedSetLocalChatFilters.cancel();
    };
    t4 = [debouncedSetLocalChatFilters];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const {
    currentUser,
    currentLocationId
  } = useUser();
  const {
    locations
  } = useLocations();
  const {
    tenant
  } = useTenant();
  let t5;
  if ($[6] !== currentUser) {
    t5 = hasPermission(currentUser, PERMISSIONS.MESSAGING_CHATS_READ);
    $[6] = currentUser;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const canViewMessages = t5;
  let t6;
  if ($[8] !== canViewMessages || $[9] !== tenant?.enabledModules) {
    t6 = canViewMessages && !!tenant?.enabledModules?.includes("TEXTING");
    $[8] = canViewMessages;
    $[9] = tenant?.enabledModules;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const chatEnabled = t6;
  const isScopeReady = useChatFiltersStore(_temp3);
  const canFetchChats = chatEnabled && isScopeReady;
  let t7;
  if ($[11] !== localChatFilters) {
    t7 = chatQueryOptions.infiniteList(localChatFilters);
    $[11] = localChatFilters;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== canFetchChats || $[14] !== t7) {
    t8 = {
      ...t7,
      enabled: canFetchChats,
      select: _temp5
    };
    $[13] = canFetchChats;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: t9,
    isLoading,
    hasNextPage: hasMoreChats,
    fetchNextPage: loadMoreChats,
    isFetchingNextPage
  } = useInfiniteQuery(t8);
  let t10;
  if ($[16] !== t9) {
    t10 = t9 === undefined ? [] : t9;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const chatList = t10;
  const chatListLoading = isLoading || chatEnabled && !isScopeReady;
  let t11;
  if ($[18] !== chatFilters.locationId) {
    t11 = chatQueryOptions.unreadCount(chatFilters.locationId);
    $[18] = chatFilters.locationId;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== canFetchChats || $[21] !== t11) {
    t12 = {
      ...t11,
      enabled: canFetchChats,
      select: _temp6
    };
    $[20] = canFetchChats;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    data: t13
  } = useQuery(t12);
  const unreadChatsCount = t13 === undefined ? 0 : t13;
  const hasMultipleLocations = locations.length > 1;
  let t14;
  if ($[23] !== chatList) {
    t14 = chatList.map(_temp7);
    $[23] = chatList;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const chatIds = t14;
  const selection = useChatSelection(chatIds, localChatFilters);
  const {
    clear: clearSelection
  } = selection;
  let t15;
  let t16;
  if ($[25] !== clearSelection || $[26] !== isExpanded) {
    t15 = () => {
      if (!isExpanded) {
        clearSelection();
      }
    };
    t16 = [isExpanded, clearSelection];
    $[25] = clearSelection;
    $[26] = isExpanded;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  const FilterChip = _temp8;
  const loaderRef = useRef(null);
  let t17;
  let t18;
  if ($[29] !== isExpanded || $[30] !== loadMoreChats) {
    t17 = () => {
      if (!isExpanded) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          loadMoreChats({
            cancelRefetch: false
          });
        }
      });
      if (loaderRef.current) {
        observer.observe(loaderRef.current);
      }
      return () => {
        if (loaderRef.current) {
          observer.unobserve(loaderRef.current);
        }
      };
    };
    t18 = [loadMoreChats, isExpanded];
    $[29] = isExpanded;
    $[30] = loadMoreChats;
    $[31] = t17;
    $[32] = t18;
  } else {
    t17 = $[31];
    t18 = $[32];
  }
  useEffect(t17, t18);
  if (!currentUser?.showMessagesBox) {
    return null;
  }
  if (!canViewMessages) {
    let t19;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Tooltip content="You have no permissions to view messages" triggerAsChild={true} side="top" sideOffset={10}><motion.div className="relative z-[99999] flex cursor-not-allowed flex-row items-center justify-between rounded-t-lg border bg-background-secondary px-2 shadow-lg" style={{
          width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
          height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT
        }}><span className="ml-2 text-body-lg font-semibold text-foreground">Messages</span></motion.div></Tooltip>;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    return t19;
  }
  let t19;
  if ($[34] !== chatFilters || $[35] !== chatList || $[36] !== chatListLoading || $[37] !== currentLocationId || $[38] !== hasMoreChats || $[39] !== hasMultipleLocations || $[40] !== isExpanded || $[41] !== isFetchingNextPage || $[42] !== localChatFilters || $[43] !== openChat || $[44] !== selection || $[45] !== setChatFilters || $[46] !== setIsAddChatOpen || $[47] !== tenant?.enabledModules || $[48] !== unreadChatsCount) {
    t19 = <AnimatePresence>{!isExpanded ? <motion.div initial={{
        width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT
      }} animate={{
        width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT
      }} exit={{
        width: GLOBAL_CHAT_CONSTANTS.CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MESSAGES_SIDEBAR_HEIGHT
      }} transition={{
        duration: 0.2
      }} className="relative z-[99999] flex flex-row items-center justify-between rounded-t-2xl border bg-background/80 px-2 shadow-lg backdrop-blur-lg" onClick={() => setIsExpanded(true)}><div className="flex items-center gap-1"><span className="ml-1 text-body-lg font-semibold text-foreground">Messages</span>{unreadChatsCount > 0 && <div className="flex h-5 min-w-5 items-center justify-center rounded-md bg-red-500 px-1.5 text-caption-strong text-white">{unreadChatsCount}</div>}</div><div className="flex items-center gap-2"><Button variant="ghost" className="p-1 text-foreground hover:text-foreground-secondary" onClick={e => {
            e.stopPropagation();
            setIsAddChatOpen(true);
          }}><RiChatNewLine className="h-4 w-4" /></Button><Button variant="ghost" size="icon-sm" className="text-foreground-secondary hover:text-foreground" icon={<motion.div animate={{
            rotate: 180
          }} transition={{
            duration: 0.2
          }}><LuChevronDown className="h-5 w-5" /></motion.div>} onClick={e_0 => {
            e_0.stopPropagation();
            setIsExpanded(true);
          }} /></div></motion.div> : <motion.div initial={{
        width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT
      }} animate={{
        width: GLOBAL_CHAT_CONSTANTS.CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MESSAGES_SIDEBAR_HEIGHT
      }} exit={{
        width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT
      }} transition={{
        duration: 0.2
      }} className="relative z-10 flex max-h-[calc(100vh-200px)] flex-col rounded-t-lg bg-background/40 shadow-lg backdrop-blur-lg"><div className="flex flex-col rounded-t-lg border bg-background/90 p-2"><div className="flex items-center justify-between"><div className="flex items-center"><span className="ml-2 text-body-lg font-semibold text-foreground">Messages</span>{unreadChatsCount > 0 && <div className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-md bg-red-500 px-1.5 text-caption-strong text-white">{unreadChatsCount}</div>}</div><div className="flex items-center gap-2"><Button variant="ghost" className="p-1 text-foreground hover:text-foreground-secondary" onClick={() => setIsAddChatOpen(true)}><RiChatNewLine className="h-4 w-4" /></Button><Button variant="ghost" className="p-1 text-foreground hover:text-foreground-secondary" onClick={() => setIsExpanded(false)}><motion.div animate={{
                  rotate: 0
                }} transition={{
                  duration: 0.2
                }}><LuChevronDown className="h-5 w-5" /></motion.div></Button></div></div><div className="mt-2 flex flex-wrap items-center gap-1"><FilterChip label="Unread" isActive={chatFilters.unreadOnly} onClick={() => setChatFilters({
              ...chatFilters,
              unreadOnly: !chatFilters.unreadOnly
            })} tooltip="Show chats with unread messages" /><FilterChip label="Unresponded" isActive={chatFilters.unrespondedOnly} onClick={() => setChatFilters({
              ...chatFilters,
              unrespondedOnly: !chatFilters.unrespondedOnly
            })} tooltip="Show chats where your response may be needed" /><FilterChip label="Human Only" isActive={chatFilters.humanOnly} onClick={() => setChatFilters({
              ...chatFilters,
              humanOnly: !chatFilters.humanOnly
            })} tooltip="Hide AI-only chats" />{hasMultipleLocations && tenant?.enabledModules?.includes("LOCATIONS") && <FilterChip label="My Location" isActive={!!chatFilters.locationId} onClick={() => setChatFilters({
              ...chatFilters,
              locationId: chatFilters.locationId ? undefined : currentLocationId || undefined
            })} tooltip={currentLocationId ? "Show conversations from your location" : "You must set a location to use this filter"} disabled={!currentLocationId} />}</div><div className="pt-3"><SelectAllRow selection={selection} /></div></div><ScrollShadow.Root><ScrollShadow.Viewport className="h-full border-x p-1">{(chatList.length > 0 || chatListLoading) && chatList.map((chat_0, index) => <MessagingChatRow key={chat_0.id} chat={chat_0} index={index} selection={selection} onOpen={() => {
              setIsAddChatOpen(false);
              openChat(undefined, chat_0.id);
            }} />)}<div ref={loaderRef} className="py-2 text-center text-body text-foreground-muted">{isFetchingNextPage ? "Loading..." : hasMoreChats ? "Scroll to load more" : chatList.length > 0 ? "No more chats" : localChatFilters.searchTerm ? "No matching conversations found" : chatListLoading ? "Loading..." : "No conversations yet"}</div></ScrollShadow.Viewport><ScrollShadow.Top /><ScrollShadow.Bottom /></ScrollShadow.Root><ChatBulkActions selection={selection} filters={localChatFilters} hasMoreChats={!!hasMoreChats} /></motion.div>}</AnimatePresence>;
    $[34] = chatFilters;
    $[35] = chatList;
    $[36] = chatListLoading;
    $[37] = currentLocationId;
    $[38] = hasMoreChats;
    $[39] = hasMultipleLocations;
    $[40] = isExpanded;
    $[41] = isFetchingNextPage;
    $[42] = localChatFilters;
    $[43] = openChat;
    $[44] = selection;
    $[45] = setChatFilters;
    $[46] = setIsAddChatOpen;
    $[47] = tenant?.enabledModules;
    $[48] = unreadChatsCount;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
};
export default MessagingSidebar;
function _temp(patient) {
  return patient.firstName;
}
function _temp2(patient_0) {
  return <li key={patient_0.id} className="text-caption">{formatName(patient_0.firstName, patient_0.lastName)}</li>;
}
function _temp3(state) {
  return state.isScopeReady;
}
function _temp4(page) {
  return page?.data ?? [];
}
function _temp5(data) {
  return data?.pages.flatMap(_temp4) ?? [];
}
function _temp6(data_0) {
  return data_0 ?? 0;
}
function _temp7(chat) {
  return chat.id;
}
function _temp8(t0) {
  const {
    label,
    isActive,
    onClick,
    tooltip,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  return <Tooltip content={tooltip} triggerAsChild={true} side="bottom" sideOffset={5}><button onClick={onClick} disabled={disabled} className={cx("flex h-6 items-center gap-1 rounded-full border px-2 py-1 text-caption-strong transition-all duration-200", isActive ? "border-primary bg-primary text-white shadow-sm" : "border-border bg-background/40 text-foreground-secondary hover:border-border hover:bg-row-hover", disabled && "cursor-not-allowed bg-muted/40 opacity-75 hover:border-border hover:bg-muted")}>{label}</button></Tooltip>;
}